// src/features/reels/components/reels-modal.tsx
import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "@tanstack/react-router";
import type { ReelData } from "./reel-card";
import { activityHrefString } from "../../activities/lib/activity-href";

import { Icon } from "@majourneys/icons";
interface ReelsModalProps {
  reels: ReelData[];
  activeIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

interface ReelSlideProps {
  reel: ReelData;
  active: boolean;
  lang: string;
  onView: () => void;
  registerRef: (el: HTMLDivElement | null) => void;
}

function resolveSources(reel: ReelData) {
  const videoUrl = reel.videoUrl?.trim();
  const thumbnail = reel.thumbnail?.trim();
  // The modal autoplays, so the first frame paints immediately — no `#t=0.5`
  // media-fragment hack (it can interfere with loop/seek and playback).
  const hasRealPoster = !!thumbnail && thumbnail !== videoUrl;
  return { posterSrc: hasRealPoster ? thumbnail : undefined, videoSrc: videoUrl || undefined };
}

function ReelSlide({ reel, active, lang, onView, registerRef }: ReelSlideProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { posterSrc, videoSrc } = resolveSources(reel);

  // Native <video controls> handle play/pause/seek/volume reliably — clicking is
  // tied to the user gesture at the browser level, immune to React re-render or
  // play()-promise interruption. Autoplay must be muted (browsers block unmuted
  // autoplay); we set muted imperatively so it stays uncontrolled and the native
  // volume button isn't fought by a React prop. The user can unmute from the bar.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active) {
      video.muted = true;
      video.currentTime = 0;
      void video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [active]);

  return (
    <div
      ref={registerRef}
      className="relative flex h-full w-full snap-start snap-always items-center justify-center overflow-hidden"
    >
      {/* Immersive blurred backdrop of the same frame (fills desktop void) */}
      {active && videoSrc ? (
        <video
          src={videoSrc}
          poster={posterSrc}
          autoPlay
          loop
          muted
          playsInline
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full scale-125 object-cover blur-2xl brightness-[0.35]"
        />
      ) : (
        <div className="absolute inset-0 bg-[#050019]" aria-hidden />
      )}

      {/* Foreground reel — phone-shaped on desktop, full-bleed on mobile */}
      <div className="relative h-full w-full overflow-hidden sm:aspect-[9/16] sm:h-full sm:w-auto sm:rounded-[28px] sm:shadow-[0_30px_90px_rgba(0,0,0,0.65)]">
        {videoSrc ? (
          <video
            ref={videoRef}
            src={videoSrc}
            poster={posterSrc}
            preload={active ? "auto" : "none"}
            loop
            playsInline
            controls={active}
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            className="h-full w-full bg-black object-cover"
          />
        ) : (
          <div className="h-full w-full bg-[#040033]" aria-hidden />
        )}

        {/* Info overlay — pointer-events-none so it never blocks the native control
            bar; interactive bits sit above the bar via bottom padding. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] bg-gradient-to-t from-[#040033]/95 via-[#040033]/55 to-transparent px-5 pb-[max(56px,calc(env(safe-area-inset-bottom)+52px))] pt-24 sm:px-6 sm:pb-14">
          <span className="pointer-events-auto inline-block rounded-full bg-white/90 px-3 py-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#040033] backdrop-blur-sm">
            {reel.category.name}
          </span>
          <h3 className="mt-3 font-heading text-[22px] font-bold leading-[1.12] tracking-[-0.015em] text-white sm:text-[24px]">
            {reel.title}
          </h3>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-sans text-[13px] font-semibold text-white/82">
              <Icon name="ri-map-pin-line" className="me-1" />
              {reel.city.name}
            </span>
            <a
              href={activityHrefString({
                lang,
                citySlug: reel.city.slug,
                categorySlug: reel.category.slug,
                segment: null,
                slug: reel.activitySlug,
              })}
              onClick={onView}
              className="pointer-events-auto inline-flex h-11 items-center justify-center rounded-full bg-accent-pink px-5 font-sans text-[13px] font-bold text-white shadow-[0_12px_26px_rgba(229,0,110,0.28)] transition hover:-translate-y-0.5 hover:bg-accent-orange-hover"
            >
              {t("reels.view_listing")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReelsModal({ reels, activeIndex, onClose, onNavigate }: ReelsModalProps) {
  const { t } = useTranslation();
  const { lang } = useParams({ strict: false }) as { lang?: string };
  const scrollerRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const lastEmittedRef = useRef<number>(activeIndex);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Initial scroll to activeIndex on mount
  useEffect(() => {
    const slide = slideRefs.current[activeIndex];
    if (slide) slide.scrollIntoView({ block: "start", behavior: "instant" as ScrollBehavior });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external activeIndex changes (prev/next buttons) into scroll position
  useEffect(() => {
    if (lastEmittedRef.current === activeIndex) return;
    const slide = slideRefs.current[activeIndex];
    if (slide) slide.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [activeIndex]);

  // IntersectionObserver: emit onNavigate as user scrolls
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const idx = slideRefs.current.findIndex((el) => el === visible.target);
        if (idx >= 0 && idx !== lastEmittedRef.current) {
          lastEmittedRef.current = idx;
          onNavigate(idx);
        }
      },
      { root: scroller, threshold: [0.6] },
    );
    slideRefs.current.forEach((el) => {
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [onNavigate, reels.length]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown" && activeIndex < reels.length - 1) {
        onNavigate(activeIndex + 1);
      }
      if (e.key === "ArrowUp" && activeIndex > 0) {
        onNavigate(activeIndex - 1);
      }
    },
    [activeIndex, onClose, onNavigate, reels.length],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-[1000] bg-[#03020d]"
      role="dialog"
      aria-modal="true"
      aria-label={t("reels.video_player")}
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close")}
        className="absolute right-4 top-[max(16px,env(safe-area-inset-top))] z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/12 text-white ring-1 ring-white/15 backdrop-blur-sm transition hover:bg-[#E5006E] sm:right-6 sm:top-6"
      >
        <Icon name="ri-close-line" className="text-2xl" />
      </button>

      {/* Up / Down nav (desktop) */}
      <div className="absolute right-3 top-1/2 z-20 hidden -translate-y-1/2 flex-col gap-2 md:flex">
        <button
          type="button"
          onClick={() => activeIndex > 0 && onNavigate(activeIndex - 1)}
          disabled={activeIndex === 0}
          aria-label={t("reels.previous_reel")}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/12 text-white ring-1 ring-white/15 backdrop-blur-sm transition hover:bg-[#E5006E] disabled:opacity-30 disabled:hover:bg-white/12"
        >
          <Icon name="ri-arrow-up-s-line" className="text-2xl" />
        </button>
        <button
          type="button"
          onClick={() => activeIndex < reels.length - 1 && onNavigate(activeIndex + 1)}
          disabled={activeIndex === reels.length - 1}
          aria-label={t("reels.next_reel")}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/12 text-white ring-1 ring-white/15 backdrop-blur-sm transition hover:bg-[#E5006E] disabled:opacity-30 disabled:hover:bg-white/12"
        >
          <Icon name="ri-arrow-down-s-line" className="text-2xl" />
        </button>
      </div>

      {/* Vertical reel feed (snap scroll) */}
      <div
        ref={scrollerRef}
        className="absolute inset-0 snap-y snap-mandatory overflow-y-scroll overscroll-contain"
        style={{ scrollbarWidth: "none" }}
      >
        {reels.map((reel, i) => (
          <ReelSlide
            key={reel.id}
            reel={reel}
            active={i === activeIndex}
            lang={lang ?? "en"}
            onView={onClose}
            registerRef={(el) => {
              slideRefs.current[i] = el;
            }}
          />
        ))}
      </div>

      {/* Progress dots (mobile) */}
      <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5 md:hidden">
        {reels.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i === activeIndex ? "w-6 bg-white" : "w-1.5 bg-white/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
